import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { ManageHeader, SearchBar, Fab, EmptyState, Field, TextInput, TextArea, SelectInput } from '../../components/mobile/ManageKit';
import { Truck, Trash2, Loader2 } from 'lucide-react';

const empty = { asset_id: '', equipment_type: '', supplier_id: '', capacity: '', status: 'available', notes: '' };

const MobileManageEquipment = () => {
  const { api, can } = useMobileAuth();
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [eq, ty, sup] = await Promise.all([
        api.get('/api/equipment'),
        api.get('/api/equipment-types'),
        api.get('/api/suppliers'),
      ]);
      setItems(eq.data || []);
      setTypes(ty.data || []);
      setSuppliers(sup.data || []);
    } catch (e) {
      toast.error('Failed to load equipment');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const supplierName = (id) => suppliers.find((s) => s.id === id)?.company_name || '';
  const openNew = () => { setEditing(null); setForm({ ...empty, supplier_id: suppliers[0]?.id || '' }); setConfirmDel(false); setOpen(true); };
  const openEdit = (eq) => { setEditing(eq); setForm({ ...empty, ...Object.fromEntries(Object.entries(eq).filter(([, v]) => v != null)) }); setConfirmDel(false); setOpen(true); };

  const save = async () => {
    if (!(form.asset_id || '').trim()) return toast.error('Asset ID is required');
    if (!form.equipment_type) return toast.error('Equipment type is required');
    if (!form.supplier_id) return toast.error('Supplier is required');
    setSaving(true);
    try {
      const payload = {
        asset_id: form.asset_id.trim(),
        equipment_type: form.equipment_type,
        supplier_id: form.supplier_id,
        capacity: form.capacity || null,
        status: form.status,
        notes: form.notes || null,
      };
      if (editing) await api.put(`/api/equipment/${editing.id}`, payload);
      else await api.post('/api/equipment', payload);
      toast.success(editing ? 'Asset updated' : 'Asset added');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/api/equipment/${editing.id}`);
      toast.success('Asset deleted');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cannot delete');
    }
  };

  const filtered = items.filter((eq) =>
    `${eq.asset_id} ${eq.equipment_type}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <MobileLayout>
      <ManageHeader title="Equipment" subtitle={`${items.length} assets`} />
      <div className="p-4 space-y-3">
        <SearchBar value={q} onChange={setQ} placeholder="Search assets..." />
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 text-[#ffd108] animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <EmptyState title="No equipment" subtitle="Tap Add to register an asset" />
        ) : (
          <div className="space-y-2" data-testid="equipment-list">
            {filtered.map((eq) => (
              <button key={eq.id} onClick={() => openEdit(eq)}
                className="w-full flex items-center gap-3 bg-[var(--m-bg-card)] border border-[var(--m-border)] rounded-lg px-4 py-3 text-left active:bg-[var(--m-bg-elevated)]"
                data-testid={`equipment-row-${eq.id}`}>
                <div className="w-9 h-9 rounded-lg bg-[var(--m-bg)] flex items-center justify-center shrink-0">
                  <Truck className="h-5 w-5 text-[#ffd108]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[var(--m-text)] font-medium truncate">{eq.asset_id}</div>
                  <div className="text-xs text-[var(--m-text-secondary)] truncate">{eq.equipment_type}{eq.capacity ? ` · ${eq.capacity}` : ''}{supplierName(eq.supplier_id) ? ` · ${supplierName(eq.supplier_id)}` : ''}</div>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${eq.status === 'available' ? 'text-green-400 bg-green-500/10' : 'text-red-400 bg-red-500/10'}`}>
                  {eq.status === 'available' ? 'Available' : 'Out'}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      <Fab onClick={openNew} label="Add" testId="equipment-add-fab" visible={can('equipment.write')} />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="bg-[var(--m-bg)] border-[var(--m-border)] rounded-t-2xl max-h-[90vh] overflow-y-auto">
          <SheetHeader><SheetTitle className="text-[var(--m-text)] text-left">{editing ? 'Edit Asset' : 'New Asset'}</SheetTitle></SheetHeader>
          <div className="py-4 space-y-3">
            <Field label="Asset ID / Rego" required><TextInput testId="eq-asset-id" value={form.asset_id} onChange={(e) => setForm({ ...form, asset_id: e.target.value })} placeholder="CR-01" /></Field>
            <Field label="Equipment type" required>
              <SelectInput testId="eq-type" value={form.equipment_type} onChange={(e) => setForm({ ...form, equipment_type: e.target.value })}
                placeholder="Select type" options={types.map((t) => ({ value: t, label: t }))} />
            </Field>
            <Field label="Supplier / Owner" required>
              <SelectInput testId="eq-supplier" value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}
                placeholder="Select supplier" options={suppliers.map((s) => ({ value: s.id, label: s.company_name }))} />
            </Field>
            <Field label="Capacity"><TextInput testId="eq-capacity" value={form.capacity || ''} onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder="20t" /></Field>
            <Field label="Status">
              <SelectInput testId="eq-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} placeholder=""
                options={[{ value: 'available', label: 'Available' }, { value: 'out_of_service', label: 'Out of service' }]} />
            </Field>
            <Field label="Notes"><TextArea testId="eq-notes" value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          </div>
          <div className="space-y-2 pb-2">
            <Button className="btn-volt w-full" onClick={save} disabled={saving} data-testid="eq-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Save changes' : 'Add asset')}
            </Button>
            {editing && (
              confirmDel ? (
                <Button variant="destructive" className="w-full" onClick={remove} data-testid="eq-delete-confirm">Tap again to confirm delete</Button>
              ) : (
                <Button variant="outline" className="w-full border-red-800 text-red-400" onClick={() => setConfirmDel(true)} data-testid="eq-delete">
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </Button>
              )
            )}
          </div>
        </SheetContent>
      </Sheet>
    </MobileLayout>
  );
};

export default MobileManageEquipment;
